// Imports
// *****************************************************
import { Fragment, MouseEvent } from 'react';
import { MainMenuInterface } from '@type/header-types';
import useWindowDimensions from '@hooks/useMediaQuery';
import { isHireRoute } from '@lib/functions/routes/isHireRoute';
import { mainMenuLinks } from './links-objects';
import { MainNavbarLink } from './MainNavbarLink';

/**
 * @description Main menu links component. We use the 'role' to determine which links to show and
 * also to check if the user is authenticated (role === undefined means the user is not authenticated).
 * Every entry renders as a plain link — the former MyJobs+ dropdown is gone; its entries live on
 * the my-jobs page top bar.
 * * **role** - user role ('NONE', 'USER', 'COMPANY') - look at mainMenuLinks object for more info
 * * **currentRoute** - current route pathname - needed to highlight the current route in the menu for React pages
 * * **locale** - the current app locale
 */
export const MainMenuLinks = (props: MainMenuInterface) => {
  // Destructuring props
  const {
    currentRoute = '',
    role,
    locale,
  } = props;

  // filter the main menu links based on the user login state
  let menusToShow = mainMenuLinks;
  if (role === undefined) {
    menusToShow = mainMenuLinks.filter((link) => link.onlyLoggedIn === false || link.onlyLoggedIn === undefined);
  }
  const { isTablet } = useWindowDimensions();
  const callIfTrusted = (event: MouseEvent<HTMLElement>, callback?: () => void) => {
    if (!callback) return;
    if (!event.nativeEvent.isTrusted) return;
    callback();
  };

  // Render component
  // - Filter the main menu links based on the user role ('NONE', 'USER', 'COMPANY')
  // *****************************************************
  return (
    <>
      { // Main menu links
        menusToShow
          .filter((item) => (isHireRoute(currentRoute) ? item.forSection?.includes('HIRE') : item.forSection?.includes('TALENT')))
          .map((link) => {
            // destructuring link object
            const {
              type,
              label,
              route: linkRoute,
              onlyMobile,
              onlyDesktop,
              icon,
              dataTestId,
              onClick,
            } = link;

            // If there is selected to show the links only on non desktop devices, return with nothing. We are using isTablet hook.
            if (!isTablet && onlyMobile) { return (<Fragment key={label} />); }
            // If there is selected to show the links only on desktop devices, return with nothing. We are using isTablet hook.
            if (onlyDesktop && isTablet) { return (<Fragment key={label} />); }

            return (
              <MainNavbarLink
                key={label}
                type={type}
                route={linkRoute}
                label={label}
                icon={icon}
                onClick={(event: MouseEvent<HTMLElement>) => callIfTrusted(event, onClick)}
                locale={locale}
                {...(dataTestId ? { 'data-test-id': dataTestId } : {})}
              />
            );
          })
      }

    </>
  );
};
